import { alpha, useTheme } from '@mui/material';
import Highcharts, { TooltipFormatterCallbackFunction } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef } from 'react';

interface IProps {
  height?: number;
  categories?: Array<string>;
  tooltipFormatter?: TooltipFormatterCallbackFunction;
  data?: Array<number | [number | string, number | null] | null | Highcharts.PointOptionsObject>;
  dataPropertyKeys?: Array<string>;
  loading?: boolean;
  // series?: Highcharts.SeriesOptionsType[];
  xAxis?: Highcharts.XAxisOptions;
  plotOptions?: Highcharts.PlotOptions;
  legend?: Highcharts.LegendOptions;
}

const LineChart = ({
  height = 400,
  categories = [],
  tooltipFormatter,
  data = [],
  xAxis = {},
  plotOptions = {},
  legend = {},
  dataPropertyKeys,
  loading,
}: IProps) => {
  const {
    palette: { primary, text, tooltipFill },
  } = useTheme();

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    if (!chartComponentRef.current?.chart) return;
    if (loading) {
      chartComponentRef.current?.chart.showLoading('Loading data...');
      return;
    }

    chartComponentRef.current?.chart.hideLoading();
  }, [chartComponentRef.current?.chart, loading]);

  const options: Highcharts.Options = {
    chart: {
      type: 'areaspline',
      height: height,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
      ...legend,
    },
    xAxis: {
      labels: {
        style: {
          color: text.secondary,
        },
      },
      lineColor: text.secondary,
      tickColor: text.secondary,
      categories: categories,
      showLastLabel: true,
      endOnTick: true,
      startOnTick: true,
      tickmarkPlacement: 'on',
      ...xAxis,
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        style: {
          color: text.secondary,
        },
      },
      lineColor: text.secondary,
      tickColor: text.secondary,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: tooltipFill.main,
      headerFormat: '',
      formatter: tooltipFormatter,
      style: {
        color: '#fff',
      },
      padding: 6,
    },
    credits: {
      enabled: true,
    },
    plotOptions: {
      series: {
        pointPlacement: 'on',
        marker: {
          enabled: false,
          fillColor: primary.main,
          lineWidth: 0.5,
          radius: 5,
        },
        keys: dataPropertyKeys,
      },
      ...plotOptions,
    },
    series: [
      {
        lineColor: primary.main,
        fillColor: {
          linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
          stops: [
            [0, alpha(primary.main, 0.05)],
            [1, alpha(primary.main, 0.2)],
          ],
        },

        type: 'areaspline',
        data: data,
      },
    ],
  };
  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />;
};

export default LineChart;
